import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import type { TeamHealthAthlete, InjuryRecordEntry } from '@/types';
import { BODY_REGIONS, REGION_FULL } from './bodyRegions';
import InjuryAccordion from './InjuryAccordion';
import DatePicker from '@/components/shared/DatePicker';
import SparklineChart from '@/components/shared/SparklineChart';

interface Props {
  athlete: TeamHealthAthlete;
  onInjuryChange: () => void;
  selectedRegion?: string | null;
  onRegionSelect?: (region: string) => void;
}

interface CheckinEntry {
  date: string;
  soreness: number;
  rpe: number;
  motivation: number;
  mental_fatigue: number;
}

const EMPTY_FORM = { body_region: '', injury_name: '', severity: 'minor', injury_date: '', notes: '' };

const TREND_METRICS = [
  { key: 'soreness' as const, label: 'Soreness', color: '#f59e0b' },
  { key: 'rpe' as const, label: 'RPE', color: '#3b82f6' },
  { key: 'motivation' as const, label: 'Motivation', color: '#22c55e' },
  { key: 'mental_fatigue' as const, label: 'Fatigue', color: '#ef4444' },
];

const AthleteHealthExpanded: React.FC<Props> = ({ athlete, onInjuryChange, selectedRegion, onRegionSelect }) => {
  const [injuries, setInjuries] = useState<InjuryRecordEntry[]>([]);
  const [loadingInjuries, setLoadingInjuries] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Check-in trends state
  const [showTrends, setShowTrends] = useState(false);
  const [trendDays, setTrendDays] = useState(7);
  const [checkinHistory, setCheckinHistory] = useState<CheckinEntry[]>([]);
  const [loadingTrends, setLoadingTrends] = useState(false);

  const fetchInjuries = useCallback(async () => {
    const result = await authenticatedFetchWithRetry<{ injuries: InjuryRecordEntry[] }>(
      getApiUrl(`/api/injury-history/${athlete.id}/`),
      { method: 'GET' },
      'fetching injury history',
    );
    if (result.success && result.data && (result.data as any).status !== 'error') {
      setInjuries(result.data.injuries);
    }
    setLoadingInjuries(false);
  }, [athlete.id]);

  useEffect(() => { fetchInjuries(); }, [fetchInjuries]);

  // Fetch check-in trends when expanded
  const fetchTrends = useCallback(async (days: number) => {
    setLoadingTrends(true);
    const result = await authenticatedFetchWithRetry<{ checkins: CheckinEntry[] }>(
      getApiUrl(`/api/team-health/checkin-history/${athlete.id}/?days=${days}`),
      { method: 'GET' },
      'fetching check-in history',
    );
    if (result.success && result.data) {
      setCheckinHistory(result.data.checkins);
    }
    setLoadingTrends(false);
  }, [athlete.id]);

  useEffect(() => {
    if (showTrends) fetchTrends(trendDays);
  }, [showTrends, trendDays, fetchTrends]);

  // When body map region is clicked and no injuries exist there, open form pre-filled.
  // Only react to selectedRegion changes — not injuries refetches — to avoid
  // reopening the form after save (selectedRegion persists in parent).
  const injuriesRef = React.useRef(injuries);
  injuriesRef.current = injuries;
  const loadingRef = React.useRef(loadingInjuries);
  loadingRef.current = loadingInjuries;

  useEffect(() => {
    if (!selectedRegion) return;
    // Defer until injuries are loaded
    if (loadingRef.current) {
      const check = setInterval(() => {
        if (!loadingRef.current) {
          clearInterval(check);
          const hasInjuries = injuriesRef.current.some(inj => inj.body_region === selectedRegion);
          if (!hasInjuries) {
            setShowForm(true);
            setFormData(prev => ({
              ...prev,
              body_region: selectedRegion,
              injury_date: prev.injury_date || new Date().toISOString().split('T')[0],
            }));
          }
        }
      }, 50);
      return () => clearInterval(check);
    }
    const hasInjuries = injuriesRef.current.some(inj => inj.body_region === selectedRegion);
    if (!hasInjuries) {
      setShowForm(true);
      setFormData(prev => ({
        ...prev,
        body_region: selectedRegion,
        injury_date: prev.injury_date || new Date().toISOString().split('T')[0],
      }));
    }
  }, [selectedRegion]);

  const handleStatusChange = async (injuryId: number, newStatus: string) => {
    const result = await authenticatedFetchWithRetry(
      getApiUrl(`/api/injury-history/${athlete.id}/${injuryId}/`),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      },
      'updating injury status',
    );
    if (result.success && (result.data as any)?.status !== 'error') {
      await fetchInjuries();
      onInjuryChange();
    }
  };

  const handleDelete = async (id: number) => {
    // Direct fetch — DELETE returns 204 No Content (empty body),
    // which authenticatedFetchWithRetry can't parse as JSON.
    const token = localStorage.getItem('access');
    const res = await fetch(getApiUrl(`/api/injury-history/${athlete.id}/${id}/`), {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (res.ok) {
      await fetchInjuries();
      onInjuryChange();
    }
  };

  const handleSave = async () => {
    if (!formData.body_region || !formData.injury_date) return;
    setSaving(true);
    const result = await authenticatedFetchWithRetry(
      getApiUrl(`/api/injury-history/${athlete.id}/`),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      },
      'creating injury record',
    );
    if (result.success && (result.data as any)?.status !== 'error') {
      setShowForm(false);
      setFormData(EMPTY_FORM);
      await fetchInjuries();
      onInjuryChange();
    }
    setSaving(false);
  };

  const checkin = athlete.latest_checkin;

  return (
    <div className="border-b border-[var(--card-border)] cgm-bg-secondary px-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Card 1: Check-in Details */}
        <div className="border border-[var(--card-border)] cgm-bg-primary">
          <button
            onClick={() => setShowTrends(!showTrends)}
            className="w-full flex items-center justify-between px-3 py-2 border-b border-[var(--card-border)] hover:cgm-bg-secondary transition-colors"
          >
            <h4 className="text-[11px] uppercase tracking-wider cgm-text-secondary">
              Latest Check-In
            </h4>
            {checkin && (
              <span className="text-[10px] cgm-text-secondary flex items-center gap-1">
                {showTrends ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Trends
              </span>
            )}
          </button>
          <div className="p-3">
            {checkin ? (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>Soreness: <span className="font-semibold">{checkin.soreness}/10</span></div>
                  <div>RPE: <span className="font-semibold">{checkin.rpe}/10</span></div>
                  <div>Motivation: <span className="font-semibold">{checkin.motivation}/10</span></div>
                  <div>Fatigue: <span className="font-semibold">{checkin.mental_fatigue}/10</span></div>
                </div>
                {checkin.regional_pain.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-wider cgm-text-secondary mb-1">Regional Pain</div>
                    {checkin.regional_pain.map((p, i) => (
                      <div key={i} className="text-xs flex justify-between py-0.5">
                        <span>{REGION_FULL[p.region] || p.region}</span>
                        <span className="text-amber-400">{p.pain_level}/10 ({p.pain_type})</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-[11px] cgm-text-secondary mt-1">
                  Reported: {checkin.report_date}
                </div>

                {/* Trends Drill-Down */}
                {showTrends && (
                  <div className="mt-3 pt-3 border-t border-[var(--card-border)]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-wider cgm-text-secondary">Trends</span>
                      <div className="flex gap-1">
                        {[7, 14, 30].map(d => (
                          <button
                            key={d}
                            onClick={(e) => { e.stopPropagation(); setTrendDays(d); }}
                            className={`text-[10px] px-1.5 py-0.5 border rounded-none ${
                              trendDays === d
                                ? 'border-[var(--accent-color)] cgm-text-primary'
                                : 'border-[var(--card-border)] cgm-text-secondary'
                            }`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>
                    </div>
                    {loadingTrends ? (
                      <div className="text-[10px] cgm-text-secondary">Loading...</div>
                    ) : checkinHistory.length < 2 ? (
                      <div className="text-[10px] cgm-text-secondary">Not enough data for trends</div>
                    ) : (
                      <div className="space-y-2">
                        {TREND_METRICS.map(m => (
                          <div key={m.key} className="flex items-center gap-2">
                            <span className="text-[10px] cgm-text-secondary w-16">{m.label}</span>
                            <SparklineChart
                              data={checkinHistory.map(c => c[m.key])}
                              color={m.color}
                              width={100}
                              height={24}
                            />
                            <span className="text-[10px] font-semibold cgm-text-primary">
                              {checkinHistory[checkinHistory.length - 1][m.key]}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm cgm-text-secondary opacity-50">No recent check-in</div>
            )}
          </div>
        </div>

        {/* Card 2: Injury History */}
        <div className="border border-[var(--card-border)] cgm-bg-primary">
          <div className="px-3 py-2 border-b border-[var(--card-border)]">
            <h4 className="text-[11px] uppercase tracking-wider cgm-text-secondary">
              Injury History
            </h4>
          </div>
          <div className="p-3">
            <InjuryAccordion
              injuries={injuries}
              loading={loadingInjuries}
              selectedRegion={selectedRegion}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
            />
          </div>
        </div>

        {/* Card 3: Log New Injury */}
        <div className="border border-[var(--card-border)] cgm-bg-primary">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--card-border)]">
            <h4 className="text-[11px] uppercase tracking-wider cgm-text-secondary">
              Log New Injury
            </h4>
            {!showForm && (
              <button
                onClick={() => { setShowForm(true); setFormData({ ...EMPTY_FORM, injury_date: new Date().toISOString().split('T')[0] }); }}
                className="text-xs cgm-text-secondary hover:cgm-text-primary flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            )}
          </div>
          <div className="p-3">
            {showForm ? (
              <div className="space-y-2">
                <select
                  value={formData.body_region}
                  onChange={e => {
                    const region = e.target.value;
                    setFormData(f => ({ ...f, body_region: region }));
                    if (region && onRegionSelect) onRegionSelect(region);
                  }}
                  className="w-full px-2 py-1.5 text-xs border border-[var(--card-border)] cgm-bg-secondary cgm-text-primary rounded-none"
                >
                  <option value="">Select region or click body map</option>
                  {BODY_REGIONS.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Injury name (e.g. IT band syndrome)"
                  value={formData.injury_name}
                  onChange={e => setFormData(f => ({ ...f, injury_name: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[var(--card-border)] cgm-bg-secondary cgm-text-primary rounded-none"
                />
                <select
                  value={formData.severity}
                  onChange={e => setFormData(f => ({ ...f, severity: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-[var(--card-border)] cgm-bg-secondary cgm-text-primary rounded-none"
                >
                  <option value="minor">Minor</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </select>
                <DatePicker
                  value={formData.injury_date}
                  onChange={date => setFormData(f => ({ ...f, injury_date: date }))}
                />
                <textarea
                  placeholder="Notes (optional)"
                  value={formData.notes}
                  onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-2 py-1.5 text-xs border border-[var(--card-border)] cgm-bg-secondary cgm-text-primary rounded-none resize-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving || !formData.body_region || !formData.injury_date}
                    className="flex-1 px-3 py-1.5 text-xs font-semibold bg-gray-900 text-white dark:bg-white dark:text-black rounded-none disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Injury'}
                  </button>
                  <button
                    onClick={() => { setShowForm(false); setFormData(EMPTY_FORM); }}
                    className="px-3 py-1.5 text-xs border border-[var(--card-border)] cgm-text-secondary rounded-none"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm cgm-text-secondary opacity-50">Click + Add to log a new injury</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AthleteHealthExpanded;
